import type { JobSalary } from "@lossdog/job-watchlists";
import type { ColumnDef, Header } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { JobSalaryLastPrice } from "@/components/job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryNetChange } from "@/components/job-salary-ticker/JobSalaryNetChange";
import { Table } from "@/components/ui/table/Table";
import TableCell from "@/components/ui/table/TableCell";
import TableHeaderCell from "@/components/ui/table/TableHeaderCell";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { BizUserRecentPositionsQueries } from "@/data/bizUserRecentPositions/bizUserRecentPositions.queries";
import { useJobsDataBatch } from "@/hooks/useJobsDataBatch";

const RecentSearchesHeaderCell = (header: Header<any, unknown>, label: string) => {
  return (
    <TableHeaderCell
      header={header}
      label={label}
      align="start"
    />
  );
};

const RecentSearchesTableCells = (info: any, key: string) => {
  const jobData: JobSalary | null = info.row.original;

  if (!jobData) {
    return (
      <TableCell
        align="start"
        className="cursor-pointer truncate"
      >
        <Typography
          size="body-3"
          variant="default"
        >
          --
        </Typography>
      </TableCell>
    );
  }

  if (key === "position") {
    return (
      <TableCell
        align="start"
        className="truncate"
      >
        <Typography
          size="body-3"
          variant="default"
        >
          {jobData.title}
        </Typography>
      </TableCell>
    );
  }

  if (key === "salary") {
    return (
      <TableCell align="start">
        <JobSalaryLastPrice jobSalary={jobData} />
      </TableCell>
    );
  }

  if (key === "change") {
    return (
      <TableCell align="start">
        <JobSalaryNetChange jobSalary={jobData} />
      </TableCell>
    );
  }

  return (
    <TableCell align="start">
      <Typography
        size="body-3"
        variant="default"
      >
        --
      </Typography>
    </TableCell>
  );
};

export const RecentSearches = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const { data: recentSearches, isLoading: isRecentSearchesLoading } =
    BizUserRecentPositionsQueries.useGetRecentPositions();

  // Use batch hook to fetch job data for all recent position IDs
  const { jobsData, isLoading: isJobsLoading } = useJobsDataBatch(recentSearches?.recentPositions || []);

  // Create columns with router access
  const columns = useMemo<ColumnDef<JobSalary | null>[]>(
    () => [
      {
        header: ({ header }) => RecentSearchesHeaderCell(header, "Position"),
        cell: (info) => RecentSearchesTableCells(info, "position"),
        accessorKey: "title",
        size: 200,
        enableSorting: false,
      },
      {
        header: ({ header }) => RecentSearchesHeaderCell(header, "Salary"),
        cell: (info) => RecentSearchesTableCells(info, "salary"),
        accessorKey: "salary",
        size: 100,
        enableSorting: false,
      },
      {
        header: ({ header }) => RecentSearchesHeaderCell(header, "Change"),
        cell: (info) => RecentSearchesTableCells(info, "change"),
        accessorKey: "netChange",
        size: 100,
        enableSorting: false,
      },
    ],
    [],
  );

  if (isRecentSearchesLoading || isJobsLoading) {
    return (
      <div className={cx("flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-out")}>
        <Typography
          size="label-4"
          variant="prominent-1"
          className="text-text-inverted-secondary uppercase"
        >
          {t("dashboard.recentSearches.title")}
        </Typography>
        <Typography
          size="body-3"
          variant="default"
        >
          Loading...
        </Typography>
      </div>
    );
  }

  if (!jobsData.length) {
    return (
      <div className={cx("flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-out")}>
        <Typography
          size="label-4"
          variant="prominent-1"
          className="text-text-inverted-secondary uppercase"
        >
          {t("dashboard.recentSearches.title")}
        </Typography>
        <Typography
          size="body-3"
          variant="default"
        >
          No recent searches
        </Typography>
      </div>
    );
  }

  return (
    <div className={cx("flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-out")}>
      <Typography
        size="label-4"
        variant="prominent-1"
        className="text-text-inverted-secondary uppercase"
      >
        {t("dashboard.recentSearches.title")}
      </Typography>
      <Table
        data={jobsData}
        columns={columns}
        onRowClick={(row) => {
          if (row?.id) {
            router.push(`/job/${row.id}`);
          }
        }}
        sorting={[]}
        onSortingChange={() => {}}
      />
    </div>
  );
};
